import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import {
  getActiveLoan,
  getAllLoans,
  getAllSalariedLoans,
  getLoanActivity,
  getLoanHistory,
  getLoanRepayment,
  liquidateLoan,
  requestLoan,
} from "store/api/loans";
import { LoanHist, LoanLiquidationPayload } from "store/api/models";
import {
  ActiveLoans,
  Loan,
  LoanActivity,
  LoanDisbursed,
  LoanHistory,
  LoanRequestResponse,
  RequestLoan,
} from "styles/types/loans";

export const getLoans = createAsyncThunk("loans/getAllLoans", async () => {
  const response = await getAllLoans();
  return response?.data?.Data;
});

export const getSalariedLoans = createAsyncThunk(
  "loans/getAllSalariedLoans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllSalariedLoans();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const getActiveLoans = createAsyncThunk(
  "loans/getActiveLoan",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getActiveLoan();
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  });

export const getHistory = createAsyncThunk(
  "loans/getLoanHistory",
  async (
    { pageNumber, pageSize }: { pageNumber: number; pageSize: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await getLoanHistory(pageNumber, pageSize);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const getActivity = createAsyncThunk(
  "loans/getLoanActivity",
  async () => {
    const response = await getLoanActivity();
    return response?.data?.Data;
  }
);

export const loanLiquidation = createAsyncThunk(
  "loans/loanLiquidation",
  async (payload: LoanLiquidationPayload, { rejectWithValue }) => {
    try {
      const response = await liquidateLoan(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const repaymentAsync = createAsyncThunk(
  "loans/repayment",
  async (loanReference: string, { rejectWithValue }) => {
    try {
      const response = await getLoanRepayment(loanReference);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError)
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
    }
  }
);

export const loanRequest = createAsyncThunk(
  "loans/requestLoan",
  async (payload: RequestLoan, { rejectWithValue }) => {
    try {
      const response = await requestLoan(payload);
      return response?.data?.Data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data || error?.message) {
          return rejectWithValue(
            error?.response?.data?.Message || error?.message
          );
        }
      }
    }
  }
);

type LoanState = {
  token: string;
  allEligibleLoans: Array<Loan>;
  allSalariedLoans: Array<Loan>;
  activeLoans: Array<LoanDisbursed>;
  loanActivity: LoanActivity;
  loanRequest: LoanRequestResponse | null;
  loanHistory: LoanHist;
  isLoading: boolean;
  isLoadingAllLoan: boolean;
  isLoadingLoanActivity: boolean;
  isLoadingLoanHistory: boolean;
  isRequestingLoan: boolean;
  error: string | null;
  errorLoanActivity: string | null;
  errorLoanRequest: string | null;
  errorLoanHistory: string | null;
};

const initialState: LoanState = {
  allEligibleLoans: [],
  allSalariedLoans: [],
  activeLoans: [],
  loanActivity: {} as LoanActivity,
  loanRequest: null,
  loanHistory: {} as unknown as LoanHist,
  token: "",
  isLoading: false,
  isLoadingAllLoan:false,
  isLoadingLoanHistory: false,
  isLoadingLoanActivity: false,
  isRequestingLoan: false,
  error: "",
  errorLoanActivity: "",
  errorLoanRequest: "",
  errorLoanHistory: "",
};

const loansSlice = createSlice({
  name: "loans",
  initialState,
  reducers: {
    getToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    removeCurrentLoanRequest(state) {
      state.loanRequest = null;
    },
    removeCurrentLoanRequestError(state) {
      state.errorLoanRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLoans.pending, (state) => {
      state.isLoadingAllLoan = true;
      state.error = null;
    });
    builder.addCase(
      getLoans.fulfilled,
      (state, action: PayloadAction<Array<Loan>>) => {
        state.isLoadingAllLoan = false;
        state.error = null;
        state.allEligibleLoans = action.payload;
      }
    );
    builder.addCase(getLoans.rejected, (state, action) => {
      state.isLoadingAllLoan = false;
      state.error = action.error.message || "Found error";
    });

    //salaried Loan
    builder.addCase(getSalariedLoans.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getSalariedLoans.fulfilled,
      (state, action: PayloadAction<Array<Loan>>) => {
        state.isLoading = false;
        state.error = null;
        state.allSalariedLoans = action.payload;
      }
    );
    builder.addCase(getSalariedLoans.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Found error";
    });

    // Loan Activity
    builder.addCase(getActivity.pending, (state) => {
      state.isLoadingLoanActivity = true;
      state.errorLoanActivity = null;
    });
    builder.addCase(
      getActivity.fulfilled,
      (state, action: PayloadAction<LoanActivity>) => {
        state.isLoadingLoanActivity = false;
        state.errorLoanActivity = null;
        state.loanActivity = action.payload;
      }
    );
    builder.addCase(getActivity.rejected, (state, action) => {
      state.isLoadingLoanActivity = false;
      state.errorLoanActivity = action.error.message || "Found error";
    });

    // Request Loan
    builder.addCase(loanRequest.pending, (state) => {
      state.isRequestingLoan = true;
      state.errorLoanRequest = null;
    });
    builder.addCase(
      loanRequest.fulfilled,
      (state, action: PayloadAction<LoanRequestResponse>) => {
        state.isRequestingLoan = false;
        state.errorLoanRequest = null;
        state.loanRequest = action.payload;
      }
    );
    builder.addCase(loanRequest.rejected, (state, action) => {
      state.isRequestingLoan = false;
      state.errorLoanRequest = action.error.message || "Found error";
    });

    // Get loan history
    builder.addCase(getHistory.pending, (state) => {
      state.isRequestingLoan = true;
      state.errorLoanRequest = null;
    });
    builder.addCase(
      getHistory.fulfilled,
      (state, action: PayloadAction<LoanHist | undefined>) => {
        state.isRequestingLoan = false;
        state.errorLoanRequest = null;
        if (action.payload) {
          state.loanHistory = action.payload;
        } else {
          state.loanHistory = {} as unknown as LoanHist;
        }
      }
    );
    builder.addCase(getHistory.rejected, (state, action) => {
      state.isRequestingLoan = false;
      state.errorLoanRequest = action.error.message || "Found error";
    });

    // Get active loans
    builder.addCase(getActiveLoans.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      getActiveLoans.fulfilled,
      (state, action: PayloadAction<ActiveLoans | any>) => {
        state.isLoading = false;
        state.error = null;
        state.activeLoans = action.payload?.Items;
      }
    );
    builder.addCase(getActiveLoans.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Found error";
    });
  },
});

export const {
  getToken,
  removeCurrentLoanRequest,
  removeCurrentLoanRequestError,
} = loansSlice.actions;

export default loansSlice.reducer;
